import React from 'react';
import DetailsTable from '../details-table';
import styles from './style.module.scss';
import { getApproxAge } from 'utils/approx-age';
import { TryScrambleText } from 'utils/text-scrambler';
import { listingPageModalIds } from 'constants/test-constants';
import _ from 'lodash';
import { validateLatitudeAndLongitude } from 'utils/geo_helper';
import dynamic from 'next/dynamic';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { getDescriptionMlsNumberLabelKey } from 'ui/mls-info/format-mls-info';
import { useTranslation } from 'next-i18next';
import { createPropertyFields, isEmptyFieldValue, PropertyFields } from '../../types';
import ExpandableText from './expandable-text';

import type Listing from 'data/listing';


/**
 * Dynamic import of WalkScore component.
 * This is done since this component is conditionally rendered.
 */
const WalkScore = dynamic(() => import('components/walk-score'), {
  loading: () => <div>Loading...</div>, // Fixme: Add a loading skeleton
});

//#region Types
type DescriptionProps = {
  /** The listing data to be used for the description details. */
  listing: Listing;
  /** Whether the description is private. Passing true will blur the description details. */
  isPrivate?: boolean;
  /** Whether to show the walk score. */
  showWalkScore?: boolean;
  /** Whether to scramble the text. */
  shouldScramble?: boolean;
  /** Callback function to handle the show description click. */
  onShowDescriptionClick?: (listing: Listing) => void;
  /** Whether to render the description paragraph text (and walk score). Defaults to `true`. */
  showText?: boolean;
  /** Whether to render the property detail tables (property details, maintenance fee, utilities). Defaults to `true`. */
  showDetails?: boolean;
  /** Whether to render the extras / inclusions block. Defaults to `true`. */
  showExtras?: boolean;
  /** Translation key for the extras block heading. Defaults to the "Extras" label. */
  extrasHeadingKey?: string;
  /** When `true`, omits the MLS® Number row from the detail table (e.g. when it is already shown elsewhere). Defaults to `false`. */
  hideMlsNumber?: boolean;
  /** When `true`, rows whose value is unavailable (null/empty) are omitted instead of showing a dash. Defaults to `false`. */
  hideEmptyFields?: boolean;
  /** When `true`, long description text is clamped with a "Show More" / "Show Less" toggle. Defaults to `false`. */
  collapsibleText?: boolean;
  /** Number of lines shown before the description text is clamped (only when `collapsibleText`). Defaults to `6`. */
  collapsedLineCount?: number;
  /**
   * Whether to show the AI translated disclaimer on listing details page when the active locale is AI-translated.
   */
  showAiTranslationDisclaimer?: boolean;
};
//#endregion

//#region Constants
const MAINTENANCE_FEE_FIELDS = [
  `heatIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_HEAT}`,
  `hydroIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_HYDRO}`,
  `waterIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_WATER}`,
  `buildingInsuranceIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_BUILDING_INSURANCE}`,
  `condoTaxesIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_CONDO_TAXES}`,
  `cableTvIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_CABLE_TV}`,
  `cacIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_CAC}`,
  `parkingIncluded#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_PARKING}`,
];

const UTILITIES_FIELDS = [
  `heat#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_HEAT}`,
  `ac#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_AC}`,
];

const SHARED_BUILDING_PROPERTIES_FIELDS = [
  `exterior#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_EXTERIOR}`,
  `garage#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_GARAGE}`,
];

const UNIT_PROPERTIES_FIELDS = [
  `ensuiteLaundry#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_ENSUITE_LAUNDRY}`,
  `balcony#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_BALCONY}`,
  `furnished#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_FURNISHED}`,
  `laundryLevel#${LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_LAUNDRY_LEVEL}`,
];

//#endregion

//#region Sub Components
function BlurredText({ text, shouldScramble, testId }: { text: string; shouldScramble: boolean; testId?: string }) {
  return <span className='blur' data-testid={testId}>{TryScrambleText(text, shouldScramble)}</span>;
}

function DescriptionText({ descriptionText, isPrivate = false, shouldScramble = false, handleShowDescription, collapsible = false, collapsedLineCount }: { descriptionText: string; isPrivate: boolean; shouldScramble: boolean; handleShowDescription: () => void; collapsible?: boolean; collapsedLineCount?: number }) {
  const { t } = useTranslation(Namespaces.COMMON);

  // The collapsible (non-private) description renders outside DetailsTable: DetailsTable styles
  // every descendant `section`/`div` (flex layout, half-width columns) which would clobber the
  // line-clamp and column width. A plain wrapper keeps the clamp behaviour intact.
  if (collapsible && !isPrivate) {
    return (
      <div className={styles.description}>
        <ExpandableText maxLines={collapsedLineCount} toggleTestId={listingPageModalIds.descriptionShowMoreToggle}>{descriptionText}</ExpandableText>
      </div>
    );
  }

  return (
    <DetailsTable isPrivate={isPrivate}>
      <div className={styles.description}>
        { isPrivate ?
          <>
            <BlurredText text={descriptionText} shouldScramble={shouldScramble} testId={listingPageModalIds.descriptionDetailText} />
            <a onClick={handleShowDescription}>{t(LOCALE_COMMON_PATHS.ADDRESS_SHOW_DESCRIPTION_LINK_TEXT)}</a>
          </>
          :
          <section>{descriptionText}</section>
        }
      </div>
    </DetailsTable>
  );
}

function DetailsSection({ detailsFields, shouldScramble }: { detailsFields: PropertyFields; shouldScramble: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section>
      {detailsFields.map((field, index) => {
        const value = typeof field.value === 'string' ? t(field.value) : field.value;
        return (
          <div key={index}>
            <span>{t(field.label)}</span>
            <span>{TryScrambleText(value, shouldScramble)}</span>
          </div>);
      })}

    </section>
  );
}
//#endregion

//#region Main Component
export default function Description({ listing, isPrivate = false, showWalkScore = false, shouldScramble = false, onShowDescriptionClick, showText = true, showDetails = true, showExtras = true, extrasHeadingKey = LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_EXTRAS, hideMlsNumber = false, hideEmptyFields = false, collapsibleText = false, collapsedLineCount, showAiTranslationDisclaimer }: DescriptionProps) {
  const { isCondo, locker, description, extras, miscellaneous, basement, driveway, mlsNum } = listing;
  const { laundryLevel, centralVac, fireplaceStove, fireplacePresent, acreage, condoExp } = miscellaneous || {};
  const hasFireplace = fireplaceStove || fireplacePresent;
  const listingProperties: Record<string, unknown> = { ...listing };
  const { t } = useTranslation(Namespaces.COMMON);
  const handleShowDescription = () => {
    onShowDescriptionClick?.(listing);
  };
  const shouldShowAiTranslationDisclaimer = (showAiTranslationDisclaimer ?? (showText && showDetails))
    && listing.localizedContent?.isAiTranslated === true;

  const latitude: number | undefined = listing?.position?.latitude;
  const longitude: number | undefined = listing?.position?.longitude;
  const canShowWalkScore = showWalkScore && latitude && longitude && validateLatitudeAndLongitude(latitude, longitude);
  const unescapedDescription = _.unescape(description || '');
  const descriptionTextEncoded: string = isPrivate ? TryScrambleText<string>(unescapedDescription, shouldScramble) || '' : unescapedDescription;
  const descriptionText = fixEncoding(descriptionTextEncoded);

  const unitFields: PropertyFields = isCondo ? [
    ...createPropertyFields(miscellaneous, t, ...UNIT_PROPERTIES_FIELDS),
    {
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_EXPOSURE,
      value: condoExp ? (condoExp as string).toUpperCase() : '',
    },
  ] : [];
  const buildingFields: PropertyFields = [
    ...createPropertyFields(listingProperties, t, ...SHARED_BUILDING_PROPERTIES_FIELDS),
    {
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_APPROX_AGE,
      value: miscellaneous?.approxAge ? getApproxAge(miscellaneous.approxAge.toString()) : null,
    },
    ...(isCondo ? [
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_STORIES,
        value: miscellaneous?.stories,
      },
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_PETS,
        value: miscellaneous?.pets,
      },
    ] : [{
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_BASEMENT,
      value: basement,
    },
    {
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_DRIVEWAY,
      value: driveway,
    },
    {
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_GARAGE_SPACES,
      value: miscellaneous?.garageParkSpaces,
    }]),
  ];
  const buildingAmenitiesFields: PropertyFields = isCondo ? (() => {
    const amenities: PropertyFields = [];
    [1, 2, 3, 4, 5, 6].forEach(num => {
      const amenity = miscellaneous && miscellaneous[`bldgAmenities${num}Out`];
      if (amenity) {
        amenities.push({ label: amenity as string, value: t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_YES) });
      }
    });
    return amenities;
  })() : [];
  const maintenanceFeeFields: PropertyFields = isCondo ? createPropertyFields(miscellaneous, t, ...MAINTENANCE_FEE_FIELDS) : [];
  const utilitiesFields: PropertyFields = createPropertyFields(listing as unknown as Record<string, unknown>, t, ...UTILITIES_FIELDS);
  const detailsFields: PropertyFields = [
    hideMlsNumber ? null : {
      label: getDescriptionMlsNumberLabelKey(listing.providerId),
      value: TryScrambleText(mlsNum, shouldScramble),
    },
    ...(!isCondo ? [
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_LAUNDRY_LEVEL,
        value: laundryLevel,
      },
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_CENTRAL_VAC,
        value: centralVac ? LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_YES : LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_NO,
      },
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_FIREPLACE,
        value: hasFireplace ? LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_YES : LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_NO,
      },
      {
        label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_ACREAGE,
        value: acreage,
      }] : []),
    isCondo ? {
      label: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_LOCKER,
      value: locker,
    } : null,
    // Unit properties for condos
    ...unitFields,
    // Building properties
    ...buildingFields,
    // Building amenities for condos
    ...buildingAmenitiesFields,
    // Utilities for non-condos
    ...(!isCondo ? utilitiesFields : []),
  ].filter(Boolean);

  const filterEmpties = (fields: PropertyFields): PropertyFields =>
    hideEmptyFields ? fields.filter(field => field && !isEmptyFieldValue(field.value)) : fields;

  const visibleDetailsFields = filterEmpties(detailsFields);
  const visibleMaintenanceFeeFields = filterEmpties(maintenanceFeeFields);
  const visibleUtilitiesFields = filterEmpties(utilitiesFields);

  return (
    <div className={styles.component}>
      {showText && (
        <>
          {descriptionText.trim().length > 0 && (
            <DescriptionText descriptionText={descriptionText} isPrivate={isPrivate} shouldScramble={shouldScramble} handleShowDescription={handleShowDescription} collapsible={collapsibleText} collapsedLineCount={collapsedLineCount} />
          )}
          {canShowWalkScore && <WalkScore latitude={listing.position.latitude} longitude={listing.position.longitude} />}
        </>
      )}
      {showDetails && (
        <DetailsTable isPrivate={isPrivate}>
          <DetailsSection detailsFields={visibleDetailsFields} shouldScramble={shouldScramble} />
          {isCondo && (
            <>
              {visibleMaintenanceFeeFields.length > 0 && (
                <>
                  <h4>{t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_MAINTENANCE_FEE)}</h4>
                  <DetailsSection detailsFields={visibleMaintenanceFeeFields} shouldScramble={shouldScramble} />
                </>
              )}
              {visibleUtilitiesFields.length > 0 && (
                <>
                  <h4>{t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_UTILITIES)}</h4>
                  <DetailsSection detailsFields={visibleUtilitiesFields} shouldScramble={shouldScramble} />
                </>
              )}
            </>
          )}
        </DetailsTable>
      )}
      {showExtras && (
        <>
          <h3 className={styles.extras}>
            {t(extrasHeadingKey)}
          </h3>
          <div className={styles.description} data-testid={listingPageModalIds.descriptionExtra}>
            {isPrivate ?
              <BlurredText text={extras} shouldScramble={shouldScramble} testId={listingPageModalIds.descriptionExtra} />
              :
              <section>{extras}</section>
            }
          </div>
        </>
      )}
      {shouldShowAiTranslationDisclaimer && (
        <p className={styles.aiTranslationDisclaimer} data-testid="ai-translated-disclaimer">
          {t(LOCALE_COMMON_PATHS.ADDRESS_AI_TRANSLATED_DISCLAIMER)}
        </p>
      )}
    </div>
  );
}
//#endregion

//#region Helper Functions
/**
 * Fixes common encoding issues in text by replacing malformed UTF-8 characters with proper punctuation.
 *
 * This function addresses common encoding problems that occur when text is incorrectly processed
 * through different character encodings, particularly when UTF-8 text is treated as Latin-1 or
 * other encodings. It converts malformed character sequences back to their intended punctuation marks.
 *
 * @param textEncoded - The text string that may contain encoding issues. Can be undefined.
 * @returns A cleaned string with proper punctuation marks. Returns an empty string if input is undefined.
 *
 * @example
 * ```typescript
 * // Fix common encoding issues
 * fixEncoding("Donâ€™t worry about â€œquotesâ€ and â€"dashesâ€")
 * // Returns: "Don't worry about "quotes" and –dashes—"
 *
 * // Handle undefined input
 * fixEncoding(undefined)
 * // Returns: ""
 *
 * // Handle already correct text
 * fixEncoding("This text is fine!")
 * // Returns: "This text is fine!"
 * ```
 *
 * @remarks
 * The function specifically handles these common encoding issues:
 * - â€™ → ' (apostrophe)
 * - â€œ → " (left double quotation mark)
 * - â€ → " (right double quotation mark)
 * - â€" → – (en dash)
 * - â€" → — (em dash)
 */
function fixEncoding(textEncoded: string | undefined): string {
  const textEncodedString = typeof textEncoded === 'string' ? textEncoded : '';
  return textEncodedString
    .replace(/â€™/g, '’')
    .replace(/â€œ/g, '“')
    .replace(/â€/g, '”')
    .replace(/â€“/g, '–')
    .replace(/â€”/g, '—');
}
//#endregion